import React, { useEffect, useRef, useState, useLayoutEffect } from "react";
import { Box } from "@mui/material";
import {
  fetchServiceworkerMediaBlobUrl,
  needsServiceworkerAuthFetch,
} from "../../utils/alertDetectionMedia";

/**
 * Renders an image from an absolute URL; uses Bearer fetch + blob when the URL is on the alert API host.
 * @param {{ href: string; alt?: string; sx?: object; onLoad?: () => void; onError?: () => void; imgSx?: object }} props
 */
export function AlertHistoryMediaImage({
  href,
  alt = "",
  sx,
  onLoad,
  onError,
  imgSx,
}) {
  const [src, setSrc] = useState("");
  const blobUrlRef = useRef("");
  const onErrorRef = useRef(onError);
  const onLoadRef = useRef(onLoad);
  useLayoutEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);
  useLayoutEffect(() => {
    onLoadRef.current = onLoad;
  }, [onLoad]);

  useEffect(() => {
    const revokeStored = () => {
      if (blobUrlRef.current.startsWith("blob:")) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = "";
      }
    };

    if (!href) {
      revokeStored();
      setSrc("");
      return undefined;
    }
    if (!needsServiceworkerAuthFetch(href)) {
      revokeStored();
      setSrc(href);
      return undefined;
    }

    let cancelled = false;
    revokeStored();

    (async () => {
      try {
        const u = await fetchServiceworkerMediaBlobUrl(href);
        if (cancelled) {
          if (u && String(u).startsWith("blob:")) URL.revokeObjectURL(u);
          return;
        }
        if (!u || !String(u).trim()) {
          setSrc("");
          onErrorRef.current?.();
          return;
        }
        if (String(u).startsWith("blob:")) blobUrlRef.current = u;
        setSrc(u);
      } catch {
        if (!cancelled) {
          setSrc("");
          onErrorRef.current?.();
        }
      }
    })();

    return () => {
      cancelled = true;
      revokeStored();
    };
  }, [href]);

  if (!src) {
    return null;
  }

  return (
    <Box sx={sx} component="span" display="inline-block" lineHeight={0}>
      <Box
        component="img"
        src={src}
        alt={alt}
        onLoad={() => {
          onLoadRef.current?.();
        }}
        onError={() => {
          onErrorRef.current?.();
        }}
        sx={{
          display: "block",
          maxWidth: "100%",
          height: "auto",
          ...imgSx,
        }}
      />
    </Box>
  );
}

/**
 * Renders a video from an absolute URL with auth when needed.
 * @param {{ href: string; poster?: string; sx?: object; videoSx?: object; controls?: boolean; muted?: boolean }} props
 */
export function AlertHistoryMediaVideo({
  href,
  poster,
  sx,
  videoSx,
  controls = true,
  muted = false,
}) {
  const [src, setSrc] = useState("");
  const blobUrlRef = useRef("");

  useEffect(() => {
    const revokeStored = () => {
      if (blobUrlRef.current.startsWith("blob:")) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = "";
      }
    };

    if (!href) {
      revokeStored();
      setSrc("");
      return undefined;
    }
    if (!needsServiceworkerAuthFetch(href)) {
      revokeStored();
      setSrc(href);
      return undefined;
    }

    let cancelled = false;
    revokeStored();

    (async () => {
      try {
        const u = await fetchServiceworkerMediaBlobUrl(href);
        if (cancelled) {
          if (u.startsWith("blob:")) URL.revokeObjectURL(u);
          return;
        }
        if (u.startsWith("blob:")) blobUrlRef.current = u;
        setSrc(u);
      } catch {
        if (!cancelled) setSrc("");
      }
    })();

    return () => {
      cancelled = true;
      revokeStored();
    };
  }, [href]);

  if (!src) return null;

  return (
    <Box sx={sx}>
      <Box
        component="video"
        src={src}
        poster={poster && String(poster).trim() ? String(poster).trim() : undefined}
        controls={controls}
        muted={muted}
        playsInline
        preload={poster && String(poster).trim() ? "none" : "metadata"}
        sx={{
          width: "100%",
          maxHeight: 360,
          borderRadius: 1,
          backgroundColor: "#000",
          ...videoSx,
        }}
      />
    </Box>
  );
}
